import React, { useState, useEffect, useRef, useMemo } from 'react';
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { FiHash, FiChevronDown, FiChevronUp, FiCheck, FiUserCheck, FiUserX } from 'react-icons/fi';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Verificacion from '../../../components/Verificacion';
import BASE_URL from '../../../config/config';
import Loader from '../../../components/Loader';
import FiltroBusqueda from '../../../components/ui/FiltroBusqueda';

const ESTADOS_TICKET = [
  { value: 'abierto', label: 'Abierto' },
  { value: 'retenido', label: 'Retenido' },
  { value: 'cerrado', label: 'Cerrado' },
];

const DEPARTAMENTOS = [
  { value: 'no_especificado', label: 'Sin Especificar' },
  { value: 'diseno', label: 'Diseño' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'desarrollo', label: 'Desarrollo' },
  { value: 'redes_sociales', label: 'Redes Sociales' },
];

const ESTADOS_CHAT = [
  { value: 'sin_responder', label: 'Sin Responder' },
  { value: 'respondido', label: 'Respondido' },
];

const PRIORIDADES = [
  { value: 'no_urgente', label: 'No Urgente' },
  { value: 'urgente', label: 'Urgente' },
];

const LABELS_ESTADO = { abierto: 'Abierto', retenido: 'Retenido', cerrado: 'Cerrado' };
const LABELS_DEPTO = { no_especificado: 'Sin Especificar', diseno: 'Diseño', marketing: 'Marketing', desarrollo: 'Desarrollo', redes_sociales: 'Redes Sociales' };
const LABELS_CHAT = { sin_responder: 'Sin Responder', respondido: 'Respondido' };
const LABELS_PRIORIDAD = { no_urgente: 'No Urgente', urgente: 'Urgente' };

const UMBRAL_SIN_ACTIVIDAD_HORAS = 120; // 5 días
const UMBRAL_ALERTA_HORAS = 4;

// Cuenta mensajes del cliente acumulados al final del chat que el staff aún no ha respondido.
const contarMensajesPendientes = (chatRaw) => {
  let chat = chatRaw;
  if (typeof chat === 'string') {
    try {
      chat = JSON.parse(chat);
    } catch (err) {
      return 0;
    }
  }
  if (!Array.isArray(chat)) return 0;
  let count = 0;
  for (let i = chat.length - 1; i >= 0; i -= 1) {
    if (chat[i].role !== 'user') break;
    count += 1;
  }
  return count;
};

const formatearFechaRelativa = (fecha) => {
  const diffMs = Date.now() - new Date(fecha).getTime();
  const minutos = Math.floor(diffMs / 60000);
  if (minutos < 1) return 'ahora';
  if (minutos < 60) return `hace ${minutos}m`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas}h`;
  const dias = Math.floor(horas / 24);
  if (dias < 30) return `hace ${dias}d`;
  return new Date(fecha).toLocaleDateString();
};

const toggleArrayValue = (arr, value, setter) => {
  setter(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
};

// dpto usa guiones bajos en los datos pero las clases CSS de color usan guiones
const claseDpto = (v) => (v === 'redes_sociales' ? 'redes-sociales' : v === 'no_especificado' ? 'no-especificado' : v);

const obtenerValorOrden = (ticket, campo) => {
  if (campo === 'createdAt') return new Date(ticket.createdAt).getTime();
  return (ticket[campo] || '').toString().toLowerCase();
};

const TicketManager = () => {
  const navigate = useNavigate();
  const [datosCargados, setDatosCargados] = useState(false);
  const [minimoTiempoCumplido, setMinimoTiempoCumplido] = useState(false);
  const [todosLosTickets, setTodosLosTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsPerPage, setTicketsPerPage] = useState(20);
  const [searchText, setSearchText] = useState('');

  // Filtros del menú lateral (multi-selección)
  const [selectedEstados, setSelectedEstados] = useState([]);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedChats, setSelectedChats] = useState([]);
  const [selectedPrioridades, setSelectedPrioridades] = useState([]);
  const [selectedTecnicos, setSelectedTecnicos] = useState([]);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Filtros rápidos (chips)
  const [soloMios, setSoloMios] = useState(false);
  const [soloSinActividad, setSoloSinActividad] = useState(false);
  const [soloAlerta, setSoloAlerta] = useState(false);

  // Orden
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Selección múltiple / acciones masivas
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [procesandoBulk, setProcesandoBulk] = useState(false);

  // Desplegables personalizados (estado/dpto/prioridad/técnico inline y acciones masivas)
  const [menuAbierto, setMenuAbierto] = useState(null); // { scope, id, campo } | null

  // Vistas guardadas
  const [vistasGuardadas, setVistasGuardadas] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ticketManagerVistas') || '[]');
    } catch (err) {
      return [];
    }
  });
  const [nombreNuevaVista, setNombreNuevaVista] = useState('');

  const [tecnicos, setTecnicos] = useState([]);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};

  // Ref para el menú lateral
  const menuRef = useRef(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mostrarFiltros && menuRef.current && !menuRef.current.contains(event.target)) {
        setMostrarFiltros(false);
      }
    };

    if (mostrarFiltros) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mostrarFiltros]);

  // Cerrar menú con Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && mostrarFiltros) {
        setMostrarFiltros(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mostrarFiltros]);

  // Cerrar cualquier desplegable abierto al hacer clic fuera o pulsar Escape
  useEffect(() => {
    if (!menuAbierto) return;
    const handleClick = (e) => {
      if (!e.target.closest('.desplegable-wrap')) setMenuAbierto(null);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setMenuAbierto(null);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuAbierto]);

  // Loader mínimo de 1 segundo
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimoTiempoCumplido(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Cargar filtros guardados desde localStorage al montar el componente
  useEffect(() => {
    try {
      const filtrosGuardados = localStorage.getItem('ticketManagerFiltros');
      if (filtrosGuardados) {
        const f = JSON.parse(filtrosGuardados);
        setSelectedEstados(f.selectedEstados || []);
        setSelectedDepts(f.selectedDepts || []);
        setSelectedChats(f.selectedChats || []);
        setSelectedPrioridades(f.selectedPrioridades || []);
        setSelectedTecnicos(f.selectedTecnicos || []);
        setFechaDesde(f.fechaDesde || '');
        setFechaHasta(f.fechaHasta || '');
        setSoloMios(!!f.soloMios);
        setSoloSinActividad(!!f.soloSinActividad);
        setSoloAlerta(!!f.soloAlerta);
        setSearchText(f.searchText || '');
      }
    } catch (err) {
      console.error('Error al cargar filtros guardados:', err);
    }
  }, []);

  // Guardar filtros en localStorage cuando cambien
  useEffect(() => {
    const filtros = {
      selectedEstados, selectedDepts, selectedChats, selectedPrioridades, selectedTecnicos,
      fechaDesde, fechaHasta, soloMios, soloSinActividad, soloAlerta, searchText
    };
    localStorage.setItem('ticketManagerFiltros', JSON.stringify(filtros));
  }, [selectedEstados, selectedDepts, selectedChats, selectedPrioridades, selectedTecnicos, fechaDesde, fechaHasta, soloMios, soloSinActividad, soloAlerta, searchText]);

  const fetchTickets = async () => {
    try {
      const respNormales = await axios.get(`${BASE_URL}/tickets/total/?page=1&limit=1000`, {
        headers: { Authorization: token }
      });
      const respArchivados = await axios.get(`${BASE_URL}/tickets/total-archivados/?page=1&limit=1000`, {
        headers: { Authorization: token }
      });

      const ticketsNormales = respNormales.data.tickets || [];
      const ticketsArchivados = respArchivados.data.tickets || [];
      const todosTickets = [...ticketsNormales, ...ticketsArchivados];
      const sorted = todosTickets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setTodosLosTickets(sorted);
    } catch (err) {
      console.error('Error al obtener tickets:', err);
    } finally {
      setDatosCargados(true);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // Fetch técnicos para el filtro
  useEffect(() => {
    const fetchTecnicos = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/usuario/tecnicos`, {
          headers: { Authorization: token }
        });
        setTecnicos(resp.data.usuarios);
      } catch (err) {
        console.error('Error al obtener técnicos:', err);
      }
    };
    fetchTecnicos();
  }, []);

  // Conteos por categoría (sobre el total, no sobre lo ya filtrado)
  const contarPorValor = (campo) => {
    const counts = {};
    todosLosTickets.forEach(t => {
      const v = t[campo];
      counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  };
  const conteoEstados = contarPorValor('estado_del_ticket');
  const conteoDepts = contarPorValor('dpto');
  const conteoChats = contarPorValor('estado_del_chat');
  const conteoPrioridades = contarPorValor('prioridad');
  const conteoTecnicos = contarPorValor('usuario_encargado');

  // Filtrado
  const ticketsFiltrados = useMemo(() => {
    const ahora = Date.now();
    const q = searchText.trim().toLowerCase();

    return todosLosTickets.filter(ticket => {
      const matchesEstado = selectedEstados.length === 0 || selectedEstados.includes(ticket.estado_del_ticket);

      const matchesSearch = !q ||
        ticket.asunto.toLowerCase().includes(q) ||
        ticket.identificador.toString().toLowerCase().includes(q) ||
        `${ticket.nombre} ${ticket.apellidos}`.toLowerCase().includes(q) ||
        (ticket.email || '').toLowerCase().includes(q) ||
        (ticket.telefono || '').toLowerCase().includes(q);

      // Si el admin/técnico tiene dpto_tecnico definido, SOLO puede ver tickets de su departamento
      const matchesDept = tokenInfo.dpto_tecnico
        ? ticket.dpto.toLowerCase() === tokenInfo.dpto_tecnico.toLowerCase()
        : (selectedDepts.length === 0 || selectedDepts.includes(ticket.dpto));

      const matchesChat = selectedChats.length === 0 || selectedChats.includes(ticket.estado_del_chat);
      const matchesPrio = selectedPrioridades.length === 0 || selectedPrioridades.includes(ticket.prioridad);
      const matchesTecnico = selectedTecnicos.length === 0 || selectedTecnicos.includes(ticket.usuario_encargado);
      const matchesMios = !soloMios || ticket.usuario_encargado === tokenInfo.usuario;

      const fechaTicket = new Date(ticket.createdAt);
      const matchesDesde = !fechaDesde || fechaTicket >= new Date(`${fechaDesde}T00:00:00`);
      const matchesHasta = !fechaHasta || fechaTicket <= new Date(`${fechaHasta}T23:59:59`);

      const horasInactivo = (ahora - new Date(ticket.updatedAt || ticket.createdAt).getTime()) / 3600000;
      const matchesSinActividad = !soloSinActividad || (ticket.estado_del_ticket !== 'cerrado' && horasInactivo >= UMBRAL_SIN_ACTIVIDAD_HORAS);
      const matchesAlerta = !soloAlerta || (ticket.estado_del_ticket !== 'cerrado' && ticket.estado_del_chat === 'sin_responder' && horasInactivo >= UMBRAL_ALERTA_HORAS);

      return matchesEstado && matchesSearch && matchesDept && matchesChat && matchesPrio &&
        matchesTecnico && matchesMios && matchesDesde && matchesHasta && matchesSinActividad && matchesAlerta;
    });
  }, [
    todosLosTickets, selectedEstados, searchText, selectedDepts, selectedChats, selectedPrioridades,
    selectedTecnicos, soloMios, soloSinActividad, soloAlerta, fechaDesde, fechaHasta,
    tokenInfo.dpto_tecnico, tokenInfo.usuario
  ]);

  // Orden
  const ticketsOrdenados = useMemo(() => {
    const arr = [...ticketsFiltrados];
    arr.sort((a, b) => {
      const va = obtenerValorOrden(a, sortField);
      const vb = obtenerValorOrden(b, sortField);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [ticketsFiltrados, sortField, sortDir]);

  // Resetear a la primera página cuando cambian filtros, orden o tamaño de página
  useEffect(() => {
    setCurrentPage(1);
  }, [ticketsFiltrados, sortField, sortDir, ticketsPerPage]);

  // Paginación
  const totalPages = Math.max(1, Math.ceil(ticketsOrdenados.length / ticketsPerPage));
  const startIdx = (currentPage - 1) * ticketsPerPage;
  const currentTickets = ticketsOrdenados.slice(startIdx, startIdx + ticketsPerPage);

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const cambiarOrden = (campo) => {
    if (sortField === campo) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(campo);
      setSortDir('asc');
    }
  };

  const indicadorOrden = (campo) => {
    if (sortField !== campo) return null;
    return sortDir === 'asc' ? <FiChevronUp className="th-sort-flecha" /> : <FiChevronDown className="th-sort-flecha" />;
  };

  // Limpiar todos los filtros
  const limpiarFiltros = () => {
    setSelectedEstados([]);
    setSelectedDepts([]);
    setSelectedChats([]);
    setSelectedPrioridades([]);
    setSelectedTecnicos([]);
    setFechaDesde('');
    setFechaHasta('');
    setSoloMios(false);
    setSoloSinActividad(false);
    setSoloAlerta(false);
    setSearchText('');
    localStorage.removeItem('ticketManagerFiltros');
  };

  // Eliminar un filtro activo específico
  const eliminarFiltro = (filtro) => {
    switch (filtro.tipo) {
      case 'estado': setSelectedEstados(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'dept': setSelectedDepts(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'chat': setSelectedChats(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'prioridad': setSelectedPrioridades(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'tecnico': setSelectedTecnicos(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'fechaDesde': setFechaDesde(''); break;
      case 'fechaHasta': setFechaHasta(''); break;
      case 'soloMios': setSoloMios(false); break;
      case 'soloSinActividad': setSoloSinActividad(false); break;
      case 'soloAlerta': setSoloAlerta(false); break;
      case 'busqueda': setSearchText(''); break;
      default: break;
    }
  };

  const obtenerFiltrosActivos = () => {
    const filtros = [];
    selectedEstados.forEach(v => filtros.push({ tipo: 'estado', valor: v, label: 'Estado', texto: LABELS_ESTADO[v] || v }));
    if (!tokenInfo.dpto_tecnico) {
      selectedDepts.forEach(v => filtros.push({ tipo: 'dept', valor: v, label: 'Dpto', texto: LABELS_DEPTO[v] || v }));
    }
    selectedChats.forEach(v => filtros.push({ tipo: 'chat', valor: v, label: 'Chat', texto: LABELS_CHAT[v] || v }));
    selectedPrioridades.forEach(v => filtros.push({ tipo: 'prioridad', valor: v, label: 'Prioridad', texto: LABELS_PRIORIDAD[v] || v }));
    selectedTecnicos.forEach(v => filtros.push({ tipo: 'tecnico', valor: v, label: 'Técnico', texto: v === 'sin_asignar' ? 'Sin Asignar' : `@${v}` }));
    if (fechaDesde) filtros.push({ tipo: 'fechaDesde', label: 'Desde', texto: fechaDesde });
    if (fechaHasta) filtros.push({ tipo: 'fechaHasta', label: 'Hasta', texto: fechaHasta });
    if (soloMios) filtros.push({ tipo: 'soloMios', label: 'Técnico', texto: 'Solo mis tickets' });
    if (soloSinActividad) filtros.push({ tipo: 'soloSinActividad', label: 'Actividad', texto: `Sin actividad ${UMBRAL_SIN_ACTIVIDAD_HORAS / 24}+ días` });
    if (soloAlerta) filtros.push({ tipo: 'soloAlerta', label: 'Alerta', texto: `Esperando respuesta +${UMBRAL_ALERTA_HORAS}h` });
    if (searchText) filtros.push({ tipo: 'busqueda', label: 'Búsqueda', texto: searchText });
    return filtros;
  };

  const filtrosActivos = obtenerFiltrosActivos();

  // Vistas guardadas: capturar/aplicar el set de filtros completo
  const capturarFiltrosActuales = () => ({
    selectedEstados, selectedDepts, selectedChats, selectedPrioridades, selectedTecnicos,
    fechaDesde, fechaHasta, soloMios, soloSinActividad, soloAlerta, searchText
  });

  const guardarVistaActual = () => {
    const nombre = nombreNuevaVista.trim();
    if (!nombre) {
      toast.error('Ponle un nombre a la vista.');
      return;
    }
    const nuevaVista = { nombre, filtros: capturarFiltrosActuales() };
    const nuevas = [...vistasGuardadas.filter(v => v.nombre !== nombre), nuevaVista];
    setVistasGuardadas(nuevas);
    localStorage.setItem('ticketManagerVistas', JSON.stringify(nuevas));
    setNombreNuevaVista('');
    toast.success(`Vista "${nombre}" guardada.`);
  };

  const aplicarVista = (vista) => {
    const f = vista.filtros;
    setSelectedEstados(f.selectedEstados || []);
    setSelectedDepts(f.selectedDepts || []);
    setSelectedChats(f.selectedChats || []);
    setSelectedPrioridades(f.selectedPrioridades || []);
    setSelectedTecnicos(f.selectedTecnicos || []);
    setFechaDesde(f.fechaDesde || '');
    setFechaHasta(f.fechaHasta || '');
    setSoloMios(!!f.soloMios);
    setSoloSinActividad(!!f.soloSinActividad);
    setSoloAlerta(!!f.soloAlerta);
    setSearchText(f.searchText || '');
  };

  const eliminarVista = (nombre) => {
    const nuevas = vistasGuardadas.filter(v => v.nombre !== nombre);
    setVistasGuardadas(nuevas);
    localStorage.setItem('ticketManagerVistas', JSON.stringify(nuevas));
  };

  // Selección múltiple
  const toggleSeleccionado = (identificador) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(identificador)) next.delete(identificador);
      else next.add(identificador);
      return next;
    });
  };

  const todosPaginaSeleccionados = currentTickets.length > 0 && currentTickets.every(t => seleccionados.has(t.identificador));

  const toggleSeleccionarTodos = () => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (todosPaginaSeleccionados) {
        currentTickets.forEach(t => next.delete(t.identificador));
      } else {
        currentTickets.forEach(t => next.add(t.identificador));
      }
      return next;
    });
  };

  // Actualización inline (una fila)
  const actualizarTicketInline = async (identificador, campo, valor, endpoint) => {
    const body = { identificador, [campo]: valor };
    try {
      await axios.put(`${BASE_URL}${endpoint}`, body, { headers: { Authorization: token } });
      setTodosLosTickets(prev => prev.map(t => t.identificador === identificador ? { ...t, [campo]: valor } : t));
    } catch (err) {
      console.error('Error al actualizar ticket:', err);
      toast.error('No se pudo actualizar el ticket.');
    }
  };

  // Acciones masivas
  const aplicarCambioMasivo = async (accion, valor) => {
    if (seleccionados.size === 0 || !valor) return;
    setProcesandoBulk(true);
    const ids = Array.from(seleccionados);
    const endpoints = {
      estado: { endpoint: '/actualizar-estado_del_ticket', campo: 'estado_del_ticket' },
      prioridad: { endpoint: '/actualizar-prioridad', campo: 'prioridad' },
      tecnico: { endpoint: '/actualizar-tecnico', campo: 'usuario_encargado' },
    };
    const { endpoint, campo } = endpoints[accion];

    try {
      await Promise.all(ids.map(id => axios.put(`${BASE_URL}${endpoint}`, { identificador: id, [campo]: valor }, { headers: { Authorization: token } })));
      setTodosLosTickets(prev => prev.map(t => ids.includes(t.identificador) ? { ...t, [campo]: valor } : t));
      toast.success(`${ids.length} ticket${ids.length > 1 ? 's' : ''} actualizado${ids.length > 1 ? 's' : ''} correctamente.`);
      setSeleccionados(new Set());
    } catch (err) {
      console.error('Error en acción masiva:', err);
      toast.error('Hubo un error al actualizar algunos tickets.');
    } finally {
      setProcesandoBulk(false);
    }
  };

  const esMenuAbierto = (scope, id, campo) =>
    !!menuAbierto && menuAbierto.scope === scope && menuAbierto.id === id && menuAbierto.campo === campo;

  const abrirMenu = (scope, id, campo) => {
    setMenuAbierto(prev => (prev && prev.scope === scope && prev.id === id && prev.campo === campo) ? null : { scope, id, campo });
  };

  // Desplegable genérico: botón que muestra el valor actual + lista de opciones marcables (sin <select> nativo)
  const renderDesplegable = ({ scope, id, campo, valorActual, opciones, claseTrigger, onSelect, triggerContent, disabled }) => {
    const abierto = esMenuAbierto(scope, id, campo);
    return (
      <div className="desplegable-wrap" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={`desplegable-trigger ${claseTrigger || 'neutro'}`}
          onClick={() => !disabled && abrirMenu(scope, id, campo)}
          disabled={disabled}
        >
          {triggerContent}
          <FiChevronDown className="desplegable-flecha" />
        </button>
        {abierto && (
          <div className="desplegable-menu">
            {opciones.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`desplegable-item ${opt.value === valorActual ? 'activo' : ''}`}
                onClick={() => { onSelect(opt.value); setMenuAbierto(null); }}
              >
                <span className="desplegable-check">{opt.value === valorActual && <FiCheck />}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!datosCargados || !minimoTiempoCumplido) {
    return (
      <div className="loader-fullscreen">
        <Loader />
      </div>
    );
  }

  if (tokenInfo.estado === "verificar") {
    return <Verificacion />;
  }

  return (
    <main className='site-width'>
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="dashboard-content gap-tickets">
        <button className="boton-volver" onClick={() => navigate('/dashboard')}>
          ← Volver
        </button>
        <div className="pagina-cabecera">
          <h2>Panel de tickets</h2>
          <p>Gestiona y supervisa todos los tickets del sistema.</p>
        </div>

        <div className="layout-tickets">
          {/* Menú lateral de filtros */}
          <div className={`sidebar-filtros-overlay ${mostrarFiltros ? 'open' : ''}`} onClick={() => setMostrarFiltros(false)} />
          <aside
            ref={menuRef}
            className={`sidebar-filtros${mostrarFiltros ? ' open' : ''}`}
          >
            <FiltroBusqueda
              title="Filtrar tickets"
              value={searchText}
              onChange={setSearchText}
              placeholder="Buscar por identificador, asunto, creador, email o teléfono..."
              className="sidebar-filtros-content"
              showInput={false}
              onClose={() => setMostrarFiltros(false)}
              showFooter={true}
              onReset={limpiarFiltros}
              resetText="Resetear filtros"
            >
              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>Estado del ticket</label>
                {ESTADOS_TICKET.map(opt => (
                  <label key={opt.value} className="checkbox-filtro">
                    <input
                      type="checkbox"
                      checked={selectedEstados.includes(opt.value)}
                      onChange={() => toggleArrayValue(selectedEstados, opt.value, setSelectedEstados)}
                    />
                    <span>{opt.label}</span>
                    <span className="checkbox-filtro-count">{conteoEstados[opt.value] || 0}</span>
                  </label>
                ))}
              </div>

              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>Departamento</label>
                {tokenInfo.dpto_tecnico ? (
                  <p className="filtro-dpto-fijo">{LABELS_DEPTO[tokenInfo.dpto_tecnico] || tokenInfo.dpto_tecnico}</p>
                ) : (
                  DEPARTAMENTOS.map(opt => (
                    <label key={opt.value} className="checkbox-filtro">
                      <input
                        type="checkbox"
                        checked={selectedDepts.includes(opt.value)}
                        onChange={() => toggleArrayValue(selectedDepts, opt.value, setSelectedDepts)}
                      />
                      <span>{opt.label}</span>
                      <span className="checkbox-filtro-count">{conteoDepts[opt.value] || 0}</span>
                    </label>
                  ))
                )}
              </div>

              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>Estado del chat</label>
                {ESTADOS_CHAT.map(opt => (
                  <label key={opt.value} className="checkbox-filtro">
                    <input
                      type="checkbox"
                      checked={selectedChats.includes(opt.value)}
                      onChange={() => toggleArrayValue(selectedChats, opt.value, setSelectedChats)}
                    />
                    <span>{opt.label}</span>
                    <span className="checkbox-filtro-count">{conteoChats[opt.value] || 0}</span>
                  </label>
                ))}
              </div>

              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>Prioridad</label>
                {PRIORIDADES.map(opt => (
                  <label key={opt.value} className="checkbox-filtro">
                    <input
                      type="checkbox"
                      checked={selectedPrioridades.includes(opt.value)}
                      onChange={() => toggleArrayValue(selectedPrioridades, opt.value, setSelectedPrioridades)}
                    />
                    <span>{opt.label}</span>
                    <span className="checkbox-filtro-count">{conteoPrioridades[opt.value] || 0}</span>
                  </label>
                ))}
              </div>

              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>Técnico asignado</label>
                <label className="checkbox-filtro">
                  <input
                    type="checkbox"
                    checked={selectedTecnicos.includes('sin_asignar')}
                    onChange={() => toggleArrayValue(selectedTecnicos, 'sin_asignar', setSelectedTecnicos)}
                  />
                  <span>Sin Asignar</span>
                  <span className="checkbox-filtro-count">{conteoTecnicos['sin_asignar'] || 0}</span>
                </label>
                {tecnicos.map(t => (
                  <label key={t.id} className="checkbox-filtro">
                    <input
                      type="checkbox"
                      checked={selectedTecnicos.includes(t.usuario)}
                      onChange={() => toggleArrayValue(selectedTecnicos, t.usuario, setSelectedTecnicos)}
                    />
                    <span>{t.nombre} {t.apellidos}</span>
                    <span className="checkbox-filtro-count">{conteoTecnicos[t.usuario] || 0}</span>
                  </label>
                ))}
              </div>

              <div className="grupo-filtro">
                <label>Rango de fechas</label>
                <div className="rango-fechas">
                  <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
                  <span>–</span>
                  <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
                </div>
              </div>

              <div className="grupo-filtro grupo-vistas">
                <label>Vistas guardadas</label>
                {vistasGuardadas.length === 0 ? (
                  <p className="vistas-vacio">No tienes vistas guardadas todavía.</p>
                ) : (
                  <div className="lista-vistas">
                    {vistasGuardadas.map(v => (
                      <div key={v.nombre} className="vista-chip">
                        <button type="button" onClick={() => aplicarVista(v)}>{v.nombre}</button>
                        <button type="button" className="vista-eliminar" onClick={() => eliminarVista(v.nombre)} aria-label={`Eliminar vista ${v.nombre}`}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="guardar-vista">
                  <input
                    type="text"
                    placeholder="Nombre de la vista..."
                    value={nombreNuevaVista}
                    onChange={e => setNombreNuevaVista(e.target.value)}
                  />
                  <button type="button" onClick={guardarVistaActual}>Guardar vista actual</button>
                </div>
              </div>
            </FiltroBusqueda>
          </aside>

          {/* Contenido principal */}
          <div className="tabcontent">
            <div className='grid-historial'>
              {/* Barra de búsqueda */}
              <div className="filtro-tickets-admin">
                <input
                  type="text"
                  placeholder="Buscar por identificador, asunto, creador, email o teléfono..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
                <div className="filtros-acciones">
                  <button
                    type="button"
                    className={`chip-toggle ${soloMios ? 'activo' : ''}`}
                    onClick={() => setSoloMios(v => !v)}
                  >
                    Mis tickets
                  </button>
                  <button
                    type="button"
                    className={`chip-toggle ${soloAlerta ? 'activo' : ''}`}
                    onClick={() => setSoloAlerta(v => !v)}
                    title={`Tickets sin responder desde hace más de ${UMBRAL_ALERTA_HORAS}h`}
                  >
                    ⏱ Esperando respuesta
                  </button>
                  <button
                    type="button"
                    className={`chip-toggle ${soloSinActividad ? 'activo' : ''}`}
                    onClick={() => setSoloSinActividad(v => !v)}
                    title={`Tickets abiertos sin movimiento en ${UMBRAL_SIN_ACTIVIDAD_HORAS / 24}+ días`}
                  >
                    Sin actividad
                  </button>
                  <select
                    className="selector-page-size"
                    value={ticketsPerPage}
                    onChange={e => setTicketsPerPage(Number(e.target.value))}
                    title="Tickets por página"
                  >
                    <option value={20}>20 / página</option>
                    <option value={50}>50 / página</option>
                    <option value={100}>100 / página</option>
                  </select>
                  <button
                    className="boton-toggle-filtros"
                    onClick={() => setMostrarFiltros(!mostrarFiltros)}
                  >
                    + Filtros
                  </button>
                </div>
              </div>

              {/* Filtros activos */}
              {filtrosActivos.length > 0 && (
                <div className="filtros-activos-container">
                  {filtrosActivos.map((filtro, index) => (
                    <div key={index} className="filtro-activo">
                      <span className="filtro-label">{filtro.label}:</span>
                      <span className="filtro-valor">{filtro.texto}</span>
                      <button
                        className="filtro-eliminar"
                        onClick={() => eliminarFiltro(filtro)}
                        title="Eliminar filtro"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Barra de acciones masivas */}
              {seleccionados.size > 0 && (
                <div className="bulk-toolbar">
                  <span className="bulk-toolbar-count">{seleccionados.size} seleccionado{seleccionados.size > 1 ? 's' : ''}</span>
                  {renderDesplegable({
                    scope: 'bulk', id: 'estado', campo: 'valor',
                    valorActual: null,
                    opciones: ESTADOS_TICKET,
                    disabled: procesandoBulk,
                    onSelect: (v) => aplicarCambioMasivo('estado', v),
                    triggerContent: 'Cambiar estado...',
                  })}
                  {renderDesplegable({
                    scope: 'bulk', id: 'prioridad', campo: 'valor',
                    valorActual: null,
                    opciones: PRIORIDADES,
                    disabled: procesandoBulk,
                    onSelect: (v) => aplicarCambioMasivo('prioridad', v),
                    triggerContent: 'Cambiar prioridad...',
                  })}
                  {renderDesplegable({
                    scope: 'bulk', id: 'tecnico', campo: 'valor',
                    valorActual: null,
                    opciones: [{ value: 'sin_asignar', label: 'Sin asignar' }, ...tecnicos.map(t => ({ value: t.usuario, label: `${t.nombre} ${t.apellidos}` }))],
                    disabled: procesandoBulk,
                    onSelect: (v) => aplicarCambioMasivo('tecnico', v),
                    triggerContent: 'Asignar técnico...',
                  })}
                  <button type="button" className="bulk-cancelar" onClick={() => setSeleccionados(new Set())} disabled={procesandoBulk}>
                    Cancelar selección
                  </button>
                </div>
              )}

              {/* Tabla de tickets */}
              <div className="overflow-tabla">
                <div className="panel-tickets">
                  <div className="ticket-list-header">
                    <span className="ticket-checkbox-header">
                      <input
                        type="checkbox"
                        checked={todosPaginaSeleccionados}
                        onChange={toggleSeleccionarTodos}
                        aria-label="Seleccionar todos los tickets de la página"
                      />
                    </span>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('asunto')}>Ticket{indicadorOrden('asunto')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('estado_del_ticket')}>Estado{indicadorOrden('estado_del_ticket')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('dpto')}>Dpto{indicadorOrden('dpto')}</button>
                    <p>Chat</p>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('prioridad')}>Prioridad{indicadorOrden('prioridad')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('createdAt')}>Fecha{indicadorOrden('createdAt')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('usuario_encargado')}>Técnico{indicadorOrden('usuario_encargado')}</button>
                  </div>

                  <div className="tickets-list">
                    {currentTickets.length === 0 ? (
                      <div className="no-tickets">No se han encontrado tickets</div>
                    ) : (
                      currentTickets.map((ticket) => {
                        const mensajesPendientes = ticket.estado_del_chat === 'sin_responder'
                          ? contarMensajesPendientes(ticket.chat)
                          : 0;
                        const horasInactivo = (Date.now() - new Date(ticket.updatedAt || ticket.createdAt).getTime()) / 3600000;
                        const enAlerta = ticket.estado_del_ticket !== 'cerrado' && ticket.estado_del_chat === 'sin_responder' && horasInactivo >= UMBRAL_ALERTA_HORAS;
                        const esUrgenteSinAsignar = ticket.prioridad === 'urgente' && ticket.usuario_encargado === 'sin_asignar' && ticket.estado_del_ticket !== 'cerrado';

                        return (
                          <div
                            key={ticket.identificador}
                            className='link-ticket'
                            role="link"
                            tabIndex={0}
                            onClick={() => navigate(`/ticket/${ticket.identificador}`)}
                            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/ticket/${ticket.identificador}`); }}
                          >
                            <div className={`ticket-item ${esUrgenteSinAsignar ? 'ticket-item-alerta' : ''}`}>
                              <div className="ticket-field ticket-checkbox" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={seleccionados.has(ticket.identificador)}
                                  onChange={() => toggleSeleccionado(ticket.identificador)}
                                  aria-label={`Seleccionar ticket ${ticket.identificador}`}
                                />
                              </div>

                              <div className="ticket-field ticket-info">
                                <span className="ticket-id-icon"><FiHash /></span>
                                <div className="ticket-info-texto">
                                  <span className="ticket-info-id">#{ticket.identificador}</span>
                                  <span className="ticket-info-asunto">
                                    {ticket.asunto.split(' ').slice(0, 5).join(' ')}
                                    {ticket.asunto.split(' ').length > 5 ? '...' : ''}
                                  </span>
                                  <span className="ticket-info-creador">{ticket.nombre} {ticket.apellidos}</span>
                                  {esUrgenteSinAsignar && <span className="badge-urgente-sin-asignar">⚠ Urgente sin asignar</span>}
                                </div>
                              </div>

                              <div className="ticket-field estado">
                                {renderDesplegable({
                                  scope: 'fila', id: ticket.identificador, campo: 'estado_del_ticket',
                                  valorActual: ticket.estado_del_ticket,
                                  opciones: ESTADOS_TICKET,
                                  claseTrigger: ticket.estado_del_ticket,
                                  onSelect: (v) => actualizarTicketInline(ticket.identificador, 'estado_del_ticket', v, '/actualizar-estado_del_ticket'),
                                  triggerContent: LABELS_ESTADO[ticket.estado_del_ticket] || ticket.estado_del_ticket,
                                })}
                              </div>

                              <div className="ticket-field dpto">
                                {renderDesplegable({
                                  scope: 'fila', id: ticket.identificador, campo: 'dpto',
                                  valorActual: ticket.dpto,
                                  opciones: DEPARTAMENTOS,
                                  claseTrigger: claseDpto(ticket.dpto),
                                  onSelect: (v) => actualizarTicketInline(ticket.identificador, 'dpto', v, '/actualizar-dpto'),
                                  triggerContent: LABELS_DEPTO[ticket.dpto] || ticket.dpto,
                                })}
                              </div>

                              <div className="ticket-field chat-estado">
                                {ticket.estado_del_chat === 'sin_responder' && (
                                  <p className='sin-responder'>
                                    Sin Responder
                                    {mensajesPendientes > 0 && <span className="chat-badge-count">{mensajesPendientes}</span>}
                                  </p>
                                )}
                                {ticket.estado_del_chat === 'respondido' && <p className='respondido'>Respondido</p>}
                                {enAlerta && (
                                  <span className="chat-alerta" title={`Esperando respuesta desde hace más de ${UMBRAL_ALERTA_HORAS}h`}>
                                    ⏱ {formatearFechaRelativa(ticket.updatedAt || ticket.createdAt)}
                                  </span>
                                )}
                              </div>

                              <div className="ticket-field prioridad">
                                {renderDesplegable({
                                  scope: 'fila', id: ticket.identificador, campo: 'prioridad',
                                  valorActual: ticket.prioridad || 'no_urgente',
                                  opciones: PRIORIDADES,
                                  claseTrigger: ticket.prioridad === 'urgente' ? 'alta' : 'sin-prioridad',
                                  onSelect: (v) => actualizarTicketInline(ticket.identificador, 'prioridad', v, '/actualizar-prioridad'),
                                  triggerContent: LABELS_PRIORIDAD[ticket.prioridad] || 'No Urgente',
                                })}
                              </div>

                              <div className="ticket-field fecha" title={new Date(ticket.createdAt).toLocaleString()}>
                                {formatearFechaRelativa(ticket.createdAt)}
                              </div>

                              <div className="ticket-field asignado">
                                {ticket.usuario_encargado === 'sin_asignar' ? (
                                  <span className="dt-asignado-icono dt-asignado-icono-vacio"><FiUserX /></span>
                                ) : (
                                  <span className="dt-asignado-icono dt-asignado-icono-activo"><FiUserCheck /></span>
                                )}
                                <span>{ticket.usuario_encargado === 'sin_asignar' ? 'Sin asignar' : `@${ticket.usuario_encargado}`}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Paginación */}
              {ticketsOrdenados.length > 0 && totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default TicketManager;
